import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import './tailwind.css';
import { CustomerDTO, InvoiceDTO, ItemLineDTO, ProductDTO, formatMoney } from './domain/models';
import { JsonInvoiceService } from './services/JsonInvoiceService';
import { CustomerForm } from './components/CustomerForm';
import { InvoiceHeader } from './components/InvoiceHeader';
import { ItemsTable } from './components/ItemsTable';
import { SummaryPanel } from './components/SummaryPanel';
import { CustomerLookup } from './components/CustomerLookup';
import { InvoicePreview } from './components/InvoicePreview';
import { ProductService } from './services/ProductService';
import { ProductPrices } from './pages/ProductPrices';
import { CustomerManagement } from './pages/CustomerManagement';
import { ApiClient } from './services/ApiClient';
import { CustomerApiService } from './services/CustomerApiService';
import { InvoiceApiService, downloadPdf, printPdf } from './services/InvoiceApiService';
import { ProductApiService } from './services/ProductApiService';
import { useToast } from './components/ToastProvider';
import { handleApiError } from './utils/handleApiError';
import { validateInvoice } from './utils/validateInvoice';
import { generateNextInvoiceNumber } from './utils/invoiceNumber';
import { Button } from './components/Button';
import { Tooltip } from './components/Tooltip';
import { useAuth } from './context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { RegisterModal } from './components/RegisterModal';

function App() {
  const { error, warning, success } = useToast();
  const auth = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState<'invoice' | 'products' | 'customers'>('invoice');
  const [showRegister, setShowRegister] = useState(false);

  // Theme selection
  const [theme, setTheme] = useState<string>(() => localStorage.getItem('theme') || 'darkgreen');
  useEffect(() => {
    if (theme === 'default') {
      delete (document.documentElement as HTMLElement).dataset.theme;
    } else {
      (document.documentElement as HTMLElement).dataset.theme = theme;
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const [customer, setCustomer] = useState<CustomerDTO>({ city: 'Berlin', country: 'DE' });
  const [invoiceNumber, setInvoiceNumber] = useState<string>(() => {
    const stored = localStorage.getItem('lastInvoiceNumber');
    if (stored) {
      return generateNextInvoiceNumber(stored);
    }
    return `${new Date().getFullYear()}-0001`;
  });
  const [issueDate, setIssueDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState<ItemLineDTO[]>([
    { product: { name: 'Beispiel-Produkt', unitPrice: 10, unit: 'Stk.', description: 'Demo' }, quantity: 1 },
  ]);
  // States für Dateinamen & Ladeindikatoren
  const [lastExportFileName, setLastExportFileName] = useState<string | null>(() => localStorage.getItem('lastExportFileName'));
  const [loadingPrint, setLoadingPrint] = useState<boolean>(false);
  const [loadingSavePdf, setLoadingSavePdf] = useState<boolean>(false);
  // Letzte verwendete Rechnungsnummer merken
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState<string | null>(() => localStorage.getItem('lastInvoiceNumber'));
  // Dialog State für Reset-Bestätigung
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  function markInvoiceNumberUsed(num: string) {
    setLastInvoiceNumber(num);
    try { localStorage.setItem('lastInvoiceNumber', num); } catch {}
  }

  // Kundenverwaltung (Backend)
  const http = useMemo(() => new ApiClient(), []);
  const customerApi = useMemo(() => new CustomerApiService(http), [http]);
  const invoiceApi = useMemo(() => new InvoiceApiService(http), [http]);
  const productApi = useMemo(() => new ProductApiService(http), [http]);

  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [errorCustomers, setErrorCustomers] = useState<string | null>(null);
  const [nextCustomerNumber, setNextCustomerNumber] = useState<string>('');

  useEffect(() => {
    if (!auth.user) return;
    let alive = true;
    setLoadingCustomers(true);
    setErrorCustomers(null);
    customerApi
      .list()
      .then((data) => { if (alive) setCustomers(Array.isArray(data) ? data : []); })
      .catch((e) => { if (alive) setErrorCustomers(String(e)); })
      .finally(() => { if (alive) setLoadingCustomers(false); });
    return () => { alive = false; };
  }, [customerApi, auth.user]);

  // Compute next suggested customer number based on the highest existing number
  useEffect(() => {
    const list = customers || [];
    let bestStr: string | null = null;
    let bestNum = -1;
    let bestMatch: RegExpExecArray | null = null;
    for (const c of list) {
      const s = (c.customerNumber || '').toString();
      if (!s) continue;
      const m = /(\d+)(?!.*\d)/.exec(s); // letztes Zahlensegment
      if (!m) continue;
      const num = Number.parseInt(m[1], 10);
      if (Number.isFinite(num) && num >= 0) {
        if (num > bestNum) {
          bestNum = num;
          bestStr = s;
          bestMatch = m;
        }
      }
    }
    if (bestStr && bestMatch) {
      const digits = bestMatch[1];
      const next = (Number.parseInt(digits, 10) + 1).toString().padStart(digits.length, '0');
      const prefix = bestStr.slice(0, bestMatch.index ?? 0);
      const suffix = bestStr.slice((bestMatch.index ?? 0) + digits.length);
      setNextCustomerNumber(`${prefix}${next}${suffix}`);
    } else {
      setNextCustomerNumber('0001');
    }
  }, [customers]);

  // Prefill current customer with suggested number if missing
  useEffect(() => {
    if (!customer?.customerNumber && nextCustomerNumber) {
      setCustomer((prev) => ({ ...prev, customerNumber: nextCustomerNumber }));
    }
  }, [nextCustomerNumber, customer?.customerNumber]);

  // Produktverwaltung: Liste via Backend laden, lokale Utils für Array-Updates
  const productService = useMemo(() => new ProductService(), []);
  const [products, setProducts] = useState<ProductDTO[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [errorProducts, setErrorProducts] = useState<string | null>(null);

  useEffect(() => {
    if (!auth.user) return;
    let alive = true;
    setLoadingProducts(true);
    setErrorProducts(null);
    productApi
      .list()
      .then((data) => { if (alive) setProducts(Array.isArray(data) ? data : []); })
      .catch((e) => { if (alive) setErrorProducts(String(e)); })
      .finally(() => { if (alive) setLoadingProducts(false); });
    return () => { alive = false; };
  }, [productApi, auth.user]);

  const upsertProduct = async (product: ProductDTO) => {
    try {
      const saved = product.id ? await productApi.update(product) : await productApi.create(product);
      setProducts(prev => productService.upsert(prev, saved));
      success('Artikel gespeichert');
    } catch (e) {
      handleApiError(e, 'Artikel speichern fehlgeschlagen', { error });
    }
  };
  const deleteProduct = async (id?: number) => {
    if (!id) return;
    try {
      await productApi.remove(id);
      setProducts(prev => productService.remove(prev, id));
      success('Artikel gelöscht');
    } catch (e) {
      handleApiError(e, 'Artikel löschen fehlgeschlagen', { error });
    }
  };

  // Kundenfunktionen (Backend)
  const upsertCustomer = async (c: CustomerDTO) => {
    try {
      if (c.id) {
        const saved = await customerApi.update(c);
        setCustomers((prev) => prev.map(x => x.id === saved.id ? saved : x));
      } else {
        const saved = await customerApi.create(c);
        setCustomers((prev) => [...prev, saved]);
      }
      success('Kunde gespeichert');
    } catch (e) {
      handleApiError(e, 'Kunde speichern fehlgeschlagen', { error });
    }
  };
  const deleteCustomerById = async (id?: number) => {
    if (!id) return;
    try {
      await customerApi.remove(id);
      setCustomers((prev) => prev.filter(c => c.id !== id));
      success('Kunde gelöscht');
    } catch (e) {
      handleApiError(e, 'Kunde löschen fehlgeschlagen', { error });
    }
  };

  // Services
  const json = useMemo(() => new JsonInvoiceService(), []);

  // Berechnungen (entfernt, da ungenutzte Werte)

  const addRow = () => {
    setItems((prev) => [...prev, { product: { name: '', unitPrice: 0, unit: 'Stk.', description: '' }, quantity: 1 }]);
  };

  const removeRow = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, patch: Partial<ItemLineDTO> | { product: Partial<ProductDTO> }) => {
    setItems((prev) => prev.map((row, i) => {
      if (i !== idx) return row;
      const next: ItemLineDTO = { ...row };
      if ('product' in patch && patch.product) {
        next.product = { ...next.product, ...patch.product } as ProductDTO;
      }
      if ('quantity' in patch && patch.quantity !== undefined) {
        next.quantity = patch.quantity as number;
      }
      return next;
    }));
  };

  const buildInvoiceDto = (): InvoiceDTO => ({
    customer,
    invoiceNumber,
    issueDate,
    items,
  });

  // Neue Memos für Validierungsstatus der Buttons
  const invoiceDto = useMemo(() => buildInvoiceDto(), [customer, invoiceNumber, issueDate, items]);
  const validationErrors = useMemo(() => validateInvoice(invoiceDto), [invoiceDto]);
  const canExport = validationErrors.length === 0 && items.length > 0; // explizit mindestens eine Position erzwingen
  const liveTotal = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.product?.unitPrice ?? 0) * Number(it.quantity ?? 0), 0),
    [items]
  );

  const exportJSON = () => {
    const dto = buildInvoiceDto();
    const fileName = `invoice-${invoiceNumber}.json`;
    json.export(dto, fileName);
    markInvoiceNumberUsed(invoiceNumber);
    setLastExportFileName(fileName);
    try { localStorage.setItem('lastExportFileName', fileName); } catch {}
  };

  const savePdfViaApi = async () => {
    setLoadingSavePdf(true);
    try {
      const dto = buildInvoiceDto();
      const valErrors = validateInvoice(dto);
      if (valErrors.length) {
        const msg = 'Validierung fehlgeschlagen:\n' + valErrors.map(e => '• ' + e).join('\n');
        error(msg);
        return;
      }
      const pdf = await invoiceApi.exportInvoice(dto);
      downloadPdf(pdf);
      const fileName = pdf.fileName || `invoice-${invoiceNumber}.pdf`;
      setLastExportFileName(fileName);
      try { localStorage.setItem('lastExportFileName', fileName); } catch {}
      markInvoiceNumberUsed(invoiceNumber);
      success('PDF gespeichert');
    } catch (e) {
      handleApiError(e, 'PDF speichern fehlgeschlagen', { error });
    } finally {
      setLoadingSavePdf(false);
    }
  };
  const printViaApi = async () => {
    setLoadingPrint(true);
    try {
      const dto = buildInvoiceDto();
      const valErrors = validateInvoice(dto);
      if (valErrors.length) {
        const msg = 'Validierung fehlgeschlagen:\n' + valErrors.map(e => '• ' + e).join('\n');
        error(msg);
        return;
      }
      const pdf = await invoiceApi.exportInvoice(dto);
      printPdf(pdf);
      // Beim Drucken kein Dateiname speichern, außer falls fileName vorhanden und noch nichts gespeichert ist
      if (!lastExportFileName) {
        const fileName = pdf.fileName || `invoice-${invoiceNumber}.pdf`;
        setLastExportFileName(fileName);
        try { localStorage.setItem('lastExportFileName', fileName); } catch {}
      }
      markInvoiceNumberUsed(invoiceNumber);
      success('PDF bereit zum Drucken');
    } catch (e) {
      handleApiError(e, 'Drucken via API fehlgeschlagen', { error });
    } finally {
      setLoadingPrint(false);
    }
  };
  const importJSON = async (file: File) => {
    try {
      const dto: InvoiceDTO = await json.import(file);
      setCustomer(dto.customer || {});
      setInvoiceNumber(dto.invoiceNumber || '');
      setIssueDate(dto.issueDate || new Date().toISOString().slice(0, 10));
      setItems(Array.isArray(dto.items) ? dto.items : []);
      success('Rechnung importiert');
    } catch (e) {
      warning('Ungültige Datei');
    }
  };


  const resetAll = () => {
    const nextNumber = generateNextInvoiceNumber(lastInvoiceNumber || invoiceNumber);
    setCustomer({ city: 'Berlin', country: 'DE' });
    setInvoiceNumber(nextNumber);
    setIssueDate(new Date().toISOString().slice(0, 10));
    setItems([]);
    markInvoiceNumberUsed(nextNumber);
  };

  const requestReset = () => {
    const hasData = (items.length > 0) || (customer.firstname || customer.lastname || customer.mail) || invoiceNumber;
    if (hasData) {
      setShowResetConfirm(true);
    } else {
      resetAll();
    }
  };

  let pageContent: React.ReactNode;
  if (page === 'invoice') {
    pageContent = (
      <>
        {/* Einleitungstext entfernt */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 items-start" data-testid="invoice-page-layout">
          <div className="flex flex-col gap-4">
            <CustomerLookup onSelect={(c) => { setCustomer(c); }} customers={customers} />
            <CustomerForm
              customer={customer}
              onChange={setCustomer}
              editable={true}
              onReset={() => setCustomer({ city: 'Berlin', country: 'DE' })}
            />
            <div className="-mt-2" data-testid="save-customer-container">
              <Tooltip content="Kunde speichern (in Datenbank aktualisieren)">
                <Button variant="primary" onClick={() => upsertCustomer(customer)} data-testid="save-customer-btn">Kunde speichern</Button>
              </Tooltip>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <InvoiceHeader
              invoiceNumber={invoiceNumber}
              issueDate={issueDate}
              onChange={(v) => {
                if (v.invoiceNumber !== undefined) setInvoiceNumber(v.invoiceNumber);
                if (v.issueDate !== undefined) setIssueDate(v.issueDate);
              }}
              onReset={requestReset}
              onPrint={printViaApi}
              onSavePdf={savePdfViaApi}
              itemCount={items.length}
              loadingPrint={loadingPrint}
              loadingSavePdf={loadingSavePdf}
              lastExportFileName={lastExportFileName}
              confirmResetVisible={showResetConfirm}
              onConfirmReset={() => { resetAll(); setShowResetConfirm(false); }}
              onCancelReset={() => setShowResetConfirm(false)}
              canExport={canExport}
              validationErrors={validationErrors}
            />
            <div className="bg-white rounded-lg shadow p-4" data-testid="invoice-quick-info">
              <h3 className="font-semibold mb-3">Status</h3>
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Rechnung</span>
                  <span className={canExport ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
                    {canExport ? 'Bereit' : 'Unvollstaendig'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Positionen</span>
                  <span>{items.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Live-Summe</span>
                  <span className="font-medium">{formatMoney(liveTotal)}</span>
                </div>
                {!canExport && validationErrors[0] && (
                  <p className="text-xs text-slate-500 pt-2 border-t border-slate-200">
                    Hinweis: {validationErrors[0]}
                  </p>
                )}
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="invoice-help-box">
              <p className="text-xs font-medium text-slate-700 mb-1">Kurzhilfe</p>
              <p className="text-xs text-slate-600">1. Kunde auswaehlen oder eingeben</p>
              <p className="text-xs text-slate-600">2. Positionen erfassen</p>
              <p className="text-xs text-slate-600">3. Drucken oder PDF speichern</p>
              <p className="text-xs text-slate-600 mt-2">Alle Pflichtfelder sollten ausgefuellt sein, bevor Export und Druck aktiv werden.</p>
            </div>
          </div>
          <div className="mt-2" data-testid="customers-loading-info">
            {loadingCustomers && <span className="ml-3 text-sm text-slate-500">Kunden werden geladen…</span>}
            {errorCustomers && <span className="ml-3 text-sm text-red-600">{errorCustomers}</span>}
          </div>
        </div>
        <ItemsTable items={items} onAdd={addRow} onRemove={removeRow} onUpdate={updateItem} products={products} onOpenProductPrices={() => setPage('products')} onSaveProduct={upsertProduct} />
        <SummaryPanel invoice={{ items }} />
        <InvoicePreview
          customer={customer}
          invoiceNumber={invoiceNumber}
          issueDate={issueDate}
          items={items}
        />
      </>
    );
  } else if (page === 'products') {
    pageContent = (
      <>
        <div className="mb-2">
          {loadingProducts && <span className="text-sm text-slate-500">Artikel werden geladen…</span>}
          {errorProducts && <span className="ml-3 text-sm text-red-600">{errorProducts}</span>}
        </div>
        <ProductPrices products={products} onSave={upsertProduct} onDelete={deleteProduct} />
      </>
    );
  } else {
    pageContent = (
      <CustomerManagement customers={customers} onSave={upsertCustomer} onDelete={deleteCustomerById} nextCustomerNumber={nextCustomerNumber} />
    );
  }

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="app-root">
      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <h1 className="text-2xl font-bold mb-4">Kassenabrechnung</h1>
        <div className="mb-6 flex gap-2 items-center">
          <Button variant={page === 'invoice' ? 'primary' : 'secondary'} onClick={() => setPage('invoice')}>Rechnung</Button>
          <Button variant={page === 'products' ? 'primary' : 'secondary'} onClick={() => setPage('products')}>Artikelpreise</Button>
          <Button variant={page === 'customers' ? 'primary' : 'secondary'} onClick={() => setPage('customers')}>Kunden</Button>
          {/* Icon Buttons für Export/Import JSON */}
          <div className="flex items-center gap-2 ml-2">
            <Tooltip content={!canExport ? (validationErrors[0] ? `Nicht bereit: ${validationErrors[0]}` : 'Nicht bereit: Pflichtfelder ausfüllen') : 'Als JSON exportieren'}>
              <Button aria-label="Export JSON" variant="secondary" disabled={!canExport} onClick={exportJSON} className="w-10 h-10 p-0 justify-center" data-testid="export-json-btn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M12 3v14"/><path d="M5 10l7 7 7-7"/><path d="M5 21h14"/></svg>
              </Button>
            </Tooltip>
            <Tooltip content="JSON importieren">
              <Button aria-label="Import JSON" variant="secondary" onClick={() => fileInputRef.current?.click()} className="w-10 h-10 p-0 justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M12 21V7"/><path d="M19 14l-7-7-7 7"/><path d="M5 3h14"/></svg>
              </Button>
            </Tooltip>
            <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importJSON(f); e.target.value=''; }} />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <label htmlFor="themeSelect" className="block text-xs font-medium text-slate-600 mb-0">Farbschema</label>
            <select id="themeSelect" className="border border-slate-300 rounded px-3 py-2 w-40 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500" value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="default">Blau</option>
              <option value="darkgreen">Dunkelgrün</option>
              <option value="emerald">Grün</option>
              <option value="amber">Orange</option>
              <option value="violet">Violett</option>
              <option value="slate">Grau</option>
            </select>
            <Tooltip content={`Abmelden (${auth.user?.username})`}>
              <Button variant="ghost" onClick={async () => { await auth.logout(); navigate('/login'); }} aria-label="Abmelden" className="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                <span className="text-sm">{auth.user?.username}</span>
              </Button>
            </Tooltip>
            {auth.isAdmin && (
              <Tooltip content="Benutzer anlegen (Admin)">
                <Button variant="ghost" onClick={() => setShowRegister(true)} aria-label="Benutzer anlegen">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                  <span className="text-sm">Benutzer</span>
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        {pageContent}
      </div>
      <RegisterModal open={showRegister} onClose={() => setShowRegister(false)} />
    </div>
  );
}

export default App;
